import { cn } from 'cn';
import { useTranslation } from 'react-i18next';

import { environment } from '~/constants/environment';

import { useQueryReleaseNotes } from '~/data/releaseNotes';

import { currentLocale } from '~/utils';

import {
  EmptyState,
  ErrorState,
  MarkdownRenderer,
  Spinner,
  withErrorBoundary,
} from '@vestigas/ui';

import { releaseNotesMarkdownConfig } from './markdownConfig';

type P = {} & ComponentStyling;

/**
 * The markdown files on which the release notes are based are managed on https://portal.azure.com/#view/Microsoft_Azure_Storage/ContainerMenuBlade/~/overview/storageAccountId/%2Fsubscriptions%2F2949f260-369c-4562-9f3b-a7585a2c0ea6%2FresourceGroups%2Frgp-storage-prod%2Fproviders%2FMicrosoft.Storage%2FstorageAccounts%2Fsavgpublicstorageprod/path/release-notes/etag/%220x8DE32595C93852E%22/defaultId//publicAccessVal/Blob
 */
export const ReleaseNotesList = withErrorBoundary(({ className, style }: P) => {
  const locale = currentLocale();
  const { t } = useTranslation('release');

  const {
    data: releaseNotes,
    error,
    isError,
    isLoading,
    isSuccess,
  } = useQueryReleaseNotes({
    environment,
    language: locale,
  });

  if (isError) {
    return (
      <div
        className={cn('flex size-full items-center justify-center', className)}
        style={style}
      >
        <ErrorState error={error} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={cn('flex size-full items-center justify-center', className)}
        style={style}
      >
        <Spinner
          title={t(($) => $.releaseNotes.loadingData.text)}
          initialDelay={600}
        />
      </div>
    );
  }

  if (isSuccess && !releaseNotes) {
    return (
      <div
        className={cn('flex size-full items-center justify-center', className)}
        style={style}
      >
        <EmptyState hint={t(($) => $.releaseNotes.emptyState.hint)} />
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      {/* <div className="ml-42 relative space-y-2 text-sm [&>*:not(h1):has(+h2)]:mb-16"> */}
      <div className="space-y-2 text-sm [&_p]:pt-0! [&>*:not(h1):has(+h2)]:mb-16">
        <MarkdownRenderer config={releaseNotesMarkdownConfig}>
          {releaseNotes}
        </MarkdownRenderer>
      </div>
    </div>
  );
});
